import { ReactElement, createContext, useEffect, useMemo, useState } from 'react';
import { MobileAppConfigInterface, MobileAppContextInterface } from '../services/exports/Interfaces';
import {
  DeviceOS,
  ENVIRONMENTS,
  OnlineShopWebViewMessageTypes,
  PushNotificationStatus,
  REDUX_STORE_SLICES,
} from '../services/exports/Constants';
import { actionCreators } from '../store/actions';
import useStore from '../hooks/store/useStore';
import ReduxActions from '../store/ReduxActions';
import { FeatureFlags } from '../modules/mobile/exports/Enums';
import collect from 'collect.js';

declare global {
  interface Window {
    mobileAppConfig?: MobileAppConfigInterface;
  }
}

interface Props {
  children: ReactElement;
}

const defaultContext: MobileAppContextInterface = {
  isMobileApp: !!window?.mobileAppConfig,
  device: null,
  isIos: false,
  isAndroid: false,
  wallets: {
    isApplePaySupported: false,
    isGooglePaySupported: false,
  },
  pushNotificationsStatus: PushNotificationStatus.Undetermined,
  featureFlags: {
    [FeatureFlags.AppReview]: false,
  },
  insets: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  session: null,
  pushNotificationPopUpOpen: false,
  setPushNotificationPopUpOpen: () => null,
  hasFeature: () => false,
  data: null,
};

export const MobileAppContext: React.Context<MobileAppContextInterface> = createContext(defaultContext);

export default function MobileAppProvider(props: Props): ReactElement {
  const { children } = props;
  const searchParams = new URLSearchParams(window.location.search);
  const isFakeMobileApp = import.meta.env.VITE_MODE !== ENVIRONMENTS.PROD && searchParams.get('mobile_app') === 'true';
  const config = window.mobileAppConfig;
  const { pushNotificationsStatus } = useStore<REDUX_STORE_SLICES.MOBILE_APP>(REDUX_STORE_SLICES.MOBILE_APP);
  const [pushNotificationPopUpOpen, setPushNotificationPopUpOpen] = useState<boolean>(false);

  const hasFeature = (feature: FeatureFlags): boolean => Boolean(collect(config?.featureFlags).get(feature));

  const context = useMemo(() => {
    if (isFakeMobileApp && !config) {
      return {
        ...defaultContext,
        isMobileApp: true,
        isIos: false,
        isAndroid: false,
        device: {} as any,
        wallets: {
          isApplePaySupported: false,
          isGooglePaySupported: false,
        },
        pushNotificationsStatus,
        pushNotificationPopUpOpen,
        setPushNotificationPopUpOpen,
        hasFeature,
      };
    }

    if (config) {
      return {
        ...config,
        isMobileApp: true,
        isIos: config.device.os === DeviceOS.iOS,
        isAndroid: config.device.os === DeviceOS.Android,
        pushNotificationsStatus,
        pushNotificationPopUpOpen,
        setPushNotificationPopUpOpen,
        hasFeature,
      };
    }

    return defaultContext;
  }, [isFakeMobileApp, config, pushNotificationsStatus, pushNotificationPopUpOpen]);

  useEffect(() => {
    const root = document.documentElement;
    if (!root || !context.isMobileApp) return;
    root.style.setProperty('--ion-safe-area-top', `${context.insets.top}px`);
    root.style.setProperty('--ion-safe-area-right', `${context.insets.right}px`);
    root.style.setProperty('--ion-safe-area-bottom', `${context.insets.bottom}px`);
    root.style.setProperty('--ion-safe-area-left', `${context.insets.left}px`);
  }, [context.isMobileApp, context.insets, isFakeMobileApp]);

  useEffect(() => {
    if (config?.session) {
      ReduxActions.dispatch(actionCreators[REDUX_STORE_SLICES.MOBILE_APP].addSession(config.session));
    }
  }, []);

  const resolveWebViewMessage = (message: any): void => {
    switch (message?.data?.type) {
      case OnlineShopWebViewMessageTypes.SetPushNotificationRequestStatus:
        return ReduxActions.dispatch(
          actionCreators[REDUX_STORE_SLICES.MOBILE_APP].update({
            pushNotificationsStatus: message.data.payload.status,
          }),
        );
      case OnlineShopWebViewMessageTypes.AppReviewRequested:
        return ReduxActions.dispatch(
          actionCreators[REDUX_STORE_SLICES.MOBILE_APP].update({
            appReviewRequested: true,
          }),
        );
      default:
        return;
    }
  };

  useEffect(() => {
    document.addEventListener('message', resolveWebViewMessage);

    return () => document.removeEventListener('message', resolveWebViewMessage);
  }, []);

  return <MobileAppContext.Provider value={context}>{children}</MobileAppContext.Provider>;
}
